import { useState } from "react";
import { AppButton } from "@libs/zapi-ui/custom";
import { Bookmark } from "lucide-react";
import { AxiosError } from "axios";
import { toast } from "sonner";
import type { TSavedFilterValues } from "@/zapi-frontend/shared/types/savedFilters";
import { useCreateSavedFilter } from "@/zapi-frontend/shared/services/useSavedFilters";
import { NamePromptModal } from "./_NamePromptModal";
import { useTranslation } from "react-i18next";

type TProps = {
  activeFilters: TSavedFilterValues;
};

function extractErrorMessage(err: unknown): string | null {
  if (err instanceof AxiosError) return err.response?.data?.message ?? err.message ?? null;
  if (err instanceof Error) return err.message;
  return null;
}

export function SaveCurrentFiltersButton({ activeFilters }: TProps) {
  const { t } = useTranslation("projects");
  const createMutation = useCreateSavedFilter();
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (name: string) => {
    setError(null);
    try {
      await createMutation.mutateAsync({ name, filters: activeFilters });
      toast.success(t("savedFilters.created"));
      setModalOpen(false);
    } catch (err) {
      setError(extractErrorMessage(err) ?? t("savedFilters.createFailed"));
    }
  };

  return (
    <>
      <AppButton
        type="button"
        appearance="contained"
        color="zinc100"
        data-testid="save-current-filters"
        onClick={() => {
          setError(null);
          setModalOpen(true);
        }}
        className="h-8 shrink-0 gap-1.5 px-3 text-[14px] font-medium shadow-none"
      >
        <Bookmark className="h-4 w-4 shrink-0 text-zinc-900" strokeWidth={1.5} />
        {t("savedFilters.save")}
      </AppButton>
      <NamePromptModal
        isOpen={modalOpen}
        title={t("savedFilters.saveTitle")}
        confirmText={t("savedFilters.save")}
        isSubmitting={createMutation.isPending}
        errorMessage={error}
        onClose={() => setModalOpen(false)}
        onConfirm={handleSave}
      />
    </>
  );
}
